import { Button, SliderField, SwitchField, TextAreaField, TextField } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useContext, useState } from 'react';
import ReactModal from 'react-modal';
import { TbTrash } from 'react-icons/tb';
import Select from 'react-select';
import { LazyTerritorySelector } from '../../custom-components/QueryEditor';
import RadiusSearchValueEditor from '../../custom-components/RadiusSearchValueEditor';
import { MemoShapeOnMap } from '../../custom-components/ShapeOnMap';
import { GlobalContext } from '../../global-context';
import General from '../../utils/GeneralUtils';
import {
  hasFilterValue,
  isFilterSet,
  isValuelessOperator,
  NavigatorFilter,
  NavigatorFilterState,
  operatorLabel,
} from './navigator-filters';

interface NavigatorFilterRowProps {
  filter: NavigatorFilter;
  state: NavigatorFilterState;
  /** The team's beacons, for the Beacons filter. */
  beacons: Edulead.EduleadV4Beacon[];
  /** The team's territories, for the Territories filter. */
  territories: Edulead.EduleadV4Territory[];
  /** The section's filters the row can turn into, by its label. */
  alternatives: NavigatorFilter[];
  /** Whether the filter's requirement is set; until it is, the row is off and says why. */
  isRequirementMet: boolean;
  onChange: (state: NavigatorFilterState) => void;
  /** The row turns into this filter, unset. */
  onSwap: (next: NavigatorFilter) => void;
  /** The row goes off the panel, its filter out of the query. */
  onRemove: () => void;
}

/** The panel's selects: the query editor's compact styles, the chosen values without a chip. */
const compactStyles = General.getCreatableStylesCompact();
const selectStyles = {
  ...compactStyles,
  multiValue: (provided: object, state: unknown) => ({
    ...compactStyles.multiValue(provided, state),
    backgroundColor: 'transparent',
    margin: 0,
  }),
  multiValueLabel: (provided: object, state: unknown) => ({
    ...compactStyles.multiValueLabel(provided, state),
    fontSize: '13px',
    paddingLeft: '6px',
    paddingRight: '2px',
  }),
  multiValueRemove: (provided: object, state: unknown) => ({
    ...compactStyles.multiValueRemove(provided, state),
    color: 'var(--amplify-colors-neutral-60)',
    ':hover': {
      backgroundColor: 'transparent',
      color: 'var(--amplify-colors-red-60)',
    },
  }),
};

/**
 * The operator's select: the same menu as the selects, the control just the operator's text and
 * arrow after the label, with no box, in the label's text.
 */
const operatorStyles = {
  ...compactStyles,
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    minHeight: 0,
    cursor: 'pointer',
  }),
  valueContainer: (provided: object) => ({
    ...provided,
    padding: 0,
  }),
  singleValue: (provided: object, state: unknown) => ({
    ...compactStyles.singleValue(provided, state),
    margin: 0,
    padding: '0 !important',
    fontSize: '13px',
    color: 'var(--amplify-colors-font-primary)',
  }),
  input: (provided: object, state: unknown) => ({
    ...compactStyles.input(provided, state),
    margin: 0,
  }),
  indicatorsContainer: (provided: object) => ({
    ...provided,
    padding: '0 !important',
  }),
  dropdownIndicator: (provided: object, state: unknown) => ({
    ...compactStyles.dropdownIndicator(provided, state),
    padding: '0 0 0 3px !important',
    color: 'var(--amplify-colors-neutral-60)',
    svg: { width: 14, height: 14 },
  }),
  menu: (provided: object, state: unknown) => ({
    ...compactStyles.menu(provided, state),
    width: 'auto',
    minWidth: '140px',
  }),
};

/** The label's select, where a row can turn into another filter: the operator's, a wider menu. */
const labelStyles = {
  ...operatorStyles,
  menu: (provided: object, state: unknown) => ({
    ...operatorStyles.menu(provided, state),
    minWidth: '200px',
  }),
};

/** The switch that turns a filter off without removing it: hidden for now (2026-11-01). */
const SHOW_FILTER_SWITCH = false;

/** Only digits, a sign and a point, as the query editor's number inputs accept. */
const isNumeric = (value: string) => value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value);

const splitList = (value: string): string[] =>
  value
    .split(',')
    .map((it) => it.trim())
    .filter((it) => !!it);

/**
 * One filter of the query: its label, its operator when it has more than one, the editor its
 * type calls for, and a way to take it out of the query once set. The editors are the
 * directories' query editor's, sized for the panel.
 */
export function NavigatorFilterRow({
  filter,
  state,
  beacons,
  territories,
  alternatives,
  isRequirementMet,
  onChange,
  onSwap,
  onRemove,
}: NavigatorFilterRowProps) {
  const globalContext = useContext(GlobalContext);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const { operator, value, disabled } = state;
  const isSet = isFilterSet(state) && isRequirementMet;

  const setOperator = (next: string) =>
    onChange({ ...state, operator: next, value: isValuelessOperator(next) ? '' : value });
  const setValue = (next: string) => onChange({ ...state, value: next });
  const toggle = () => onChange({ ...state, disabled: !disabled });

  const renderEditor = () => {
    if (isValuelessOperator(operator)) return null;

    switch (filter.type) {
      case 'text':
        return (
          <TextField
            label={filter.label}
            labelHidden
            size="small"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        );

      case 'number': {
        if (operator === 'between') {
          const [from = '', to = ''] = value.split(',');
          const setPart = (index: 0 | 1, part: string) => {
            if (!isNumeric(part)) return;
            const parts = [from, to];
            parts[index] = part;
            setValue(parts.join(','));
          };
          return (
            <div className="navigator-filter__between">
              <TextField
                label={`${filter.label} from`}
                labelHidden
                size="small"
                inputMode="decimal"
                value={from}
                onChange={(e) => setPart(0, e.target.value)}
              />
              <span>and</span>
              <TextField
                label={`${filter.label} to`}
                labelHidden
                size="small"
                inputMode="decimal"
                value={to}
                onChange={(e) => setPart(1, e.target.value)}
              />
            </div>
          );
        }
        return (
          <TextField
            label={filter.label}
            labelHidden
            size="small"
            inputMode="decimal"
            value={value}
            onChange={(e) => isNumeric(e.target.value) && setValue(e.target.value)}
          />
        );
      }

      case 'select': {
        // The same control as the lists below, with one value.
        const options = filter.values?.map((v) => ({ value: v.name, label: v.label })) ?? [];
        return (
          <Select
            value={options.find((it) => it.value === value) ?? null}
            onChange={(selected) => setValue(selected?.value ?? '')}
            styles={selectStyles}
            options={options}
            isClearable
            placeholder="Any"
          />
        );
      }

      case 'creatable':
        return (
          <Select
            value={splitList(value).map((it) => ({
              value: it,
              label: filter.getValue ? filter.getValue(it) : it,
            }))}
            onChange={(selected) => setValue(selected.map((it) => it.value).join(','))}
            isMulti
            styles={selectStyles}
            options={filter.values?.map((v) => ({ value: v.name, label: v.label }))}
            isClearable={false}
            placeholder="Any"
          />
        );

      case 'textarea':
        return (
          <TextAreaField
            label={filter.label}
            labelHidden
            size="small"
            rows={1}
            maxLength={10000}
            resize="vertical"
            placeholder={filter.placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        );

      case 'territory':
        return (
          <LazyTerritorySelector
            value={value}
            onChange={setValue}
            authInfo={globalContext?.authInfo}
          />
        );

      case 'beacons':
        return (
          <Select
            value={splitList(value).map((id) => ({
              value: id,
              label: beacons.find((b) => b.id === id)?.name ?? id,
            }))}
            onChange={(selected) => setValue(selected.map((it) => it.value).join(','))}
            isMulti
            styles={selectStyles}
            options={beacons.flatMap((b) => (b.id ? [{ value: b.id, label: b.name ?? '' }] : []))}
            isClearable={false}
            placeholder="Any"
            noOptionsMessage={() => 'No beacons'}
          />
        );

      case 'territories': {
        // Each territory with its color, as the Territories mode's list shows it.
        const option = (id: string) => {
          const territory = territories.find((t) => t.id === id);
          return { value: id, label: territory?.name ?? id, color: territory?.color ?? 1 };
        };
        return (
          <Select
            value={splitList(value).map(option)}
            onChange={(selected) => setValue(selected.map((it) => it.value).join(','))}
            isMulti
            styles={selectStyles}
            options={territories.flatMap((t) => (t.id ? [option(t.id)] : []))}
            formatOptionLabel={(it) => (
              <span className="navigator-filter__territory">
                <span
                  className="navigator-filter__swatch"
                  style={{ backgroundColor: `var(--territory-color-${it.color})` }}
                />
                {it.label}
              </span>
            )}
            isClearable={false}
            placeholder="Any"
            noOptionsMessage={() => 'No territories'}
          />
        );
      }

      case 'radiusSearch': {
        const parsed = General.decode(value) as {
          address?: string;
          radiusMiles?: number;
        } | null;
        return (
          <>
            <Button size="small" className="tiny" onClick={() => setIsMapOpen(true)}>
              {parsed?.address
                ? `${parsed.radiusMiles} mi around ${parsed.address}`
                : 'Pick a point…'}
            </Button>
            <ReactModal
              isOpen={isMapOpen}
              onRequestClose={() => setIsMapOpen(false)}
              style={General.getModalDefaults({ width: '520px' })}
              closeTimeoutMS={200}
              parentSelector={General.getModalRoot}
            >
              <RadiusSearchValueEditor value={value} handleOnChange={setValue} />
              <div className="navigator-filter__modal-actions">
                <Button size="small" variation="primary" onClick={() => setIsMapOpen(false)}>
                  Done
                </Button>
              </div>
            </ReactModal>
          </>
        );
      }

      case 'slider': {
        if (!filter.range) return null;
        const { min, max, step } = filter.range;
        return (
          <SliderField
            label={filter.label}
            labelHidden
            isValueHidden
            size="small"
            min={min}
            max={max}
            step={step}
            value={value === '' ? min : Number(value)}
            isDisabled={!isRequirementMet}
            // The minimum is the filter off: no rule.
            onChange={(next) => setValue(next === min ? '' : String(next))}
          />
        );
      }

      case 'map': {
        const parsed = General.decode(value) as { shapes?: unknown[] } | null;
        const count = Array.isArray(parsed?.shapes) ? parsed.shapes.length : 0;
        return (
          <>
            <Button size="small" className="tiny" onClick={() => setIsMapOpen(true)}>
              {count > 0 ? `${count} shape${count === 1 ? '' : 's'} drawn` : 'Draw on map…'}
            </Button>
            <ReactModal
              isOpen={isMapOpen}
              onRequestClose={() => setIsMapOpen(false)}
              style={General.getModalDefaults({ width: '930px' })}
              closeTimeoutMS={200}
              parentSelector={General.getModalRoot}
            >
              <div style={{ width: 890, height: 440, position: 'relative' }}>
                {/* The map reads its shapes once, as the query editor's does. */}
                <MemoShapeOnMap
                  searchParams={parsed ? { shapes: { json: parsed } } : undefined}
                  onChange={(shapes) => setValue(shapes ?? '')}
                />
              </div>
              <div className="navigator-filter__modal-actions">
                <Button size="small" variation="primary" onClick={() => setIsMapOpen(false)}>
                  Done
                </Button>
              </div>
            </ReactModal>
          </>
        );
      }
    }
  };

  return (
    <div
      className={`navigator-filter ${isSet ? 'navigator-filter--set' : ''} ${
        disabled ? 'navigator-filter--disabled' : ''
      }`}
    >
      <div className="navigator-filter__header">
        <Select
          className="navigator-filter__label"
          aria-label="Filter"
          value={{ value: filter.field, label: filter.label }}
          onChange={(selected) => {
            const next = alternatives.find((f) => selected && f.field === selected.value);
            if (next) onSwap(next);
          }}
          options={alternatives.map((f) => ({ value: f.field, label: f.label }))}
          styles={labelStyles}
          isSearchable={false}
        />
        {filter.range && isRequirementMet && (
          <span className="navigator-filter__reading">
            {value === '' ? filter.range.minLabel : `${value} ${filter.range.unit}`}
          </span>
        )}
        {filter.operators.length > 1 && (
          <Select
            className="navigator-filter__operator"
            aria-label={`${filter.label} operator`}
            value={{ value: operator, label: operatorLabel(operator) }}
            onChange={(selected) => selected && setOperator(selected.value)}
            options={filter.operators.map((op) => ({ value: op, label: operatorLabel(op) }))}
            styles={operatorStyles}
            isSearchable={false}
          />
        )}
        {SHOW_FILTER_SWITCH && (hasFilterValue(state) || disabled) && (
          <SwitchField
            className="navigator-filter__toggle"
            label={disabled ? 'Enable filter' : 'Disable filter'}
            isLabelHidden
            size="small"
            isChecked={!disabled}
            onChange={toggle}
          />
        )}
        <Button
          size="small"
          className="silent tiny navigator-filter__remove"
          title="Remove filter"
          onClick={onRemove}
        >
          <TbTrash />
        </Button>
      </div>
      <div className="navigator-filter__editor">{renderEditor()}</div>
      {!isRequirementMet && filter.requiresHint && (
        <div className="navigator-filter__hint">{filter.requiresHint}</div>
      )}
    </div>
  );
}
